import React, { useState, useRef } from 'react';
import { importHrTimesheetXlsx } from '../../services/api';
import { money } from './tsFormat';

/* Загрузка Excel-табеля в два шага: сначала сервер разбирает файл и
   показывает, что попадёт в какие месяцы и где файл расходится сам с собой
   (строка «Итог»), и только потом — запись. Табель ведут руками годами, и
   загружать его вслепую нельзя. */

const TimesheetImportModal = ({ isAdmin, onClose, onImported }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const pick = async (picked) => {
    if (!picked) return;
    setFile(picked);
    setPreview(null);
    setError(null);
    setBusy(true);
    try {
      const res = await importHrTimesheetXlsx(picked, true);
      setPreview(res.preview);
    } catch (e) {
      setError(e.response?.data?.error || 'Не удалось разобрать файл');
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await importHrTimesheetXlsx(file, false);
      onImported(res);
    } catch (e) {
      setError(e.response?.data?.error || 'Загрузка не удалась');
      setBusy(false);
    }
  };

  const months = preview?.months || [];
  const last = months[months.length - 1];
  const mismatched = months.filter(m => m.file_total !== null && Math.abs(m.halves - m.file_total) > 1);
  const fileErrors = months.flatMap(m => m.file_errors.map(err => ({ ...err, month: m.label })));

  return (
    <div className="ui-modal" onClick={busy ? undefined : onClose}>
      <div className="ui-modal-box wide ts-import" onClick={(e) => e.stopPropagation()}>
        <div className="ui-modal-head">
          <h2>Загрузить табель из Excel</h2>
          <button className="ui-icon-btn" onClick={onClose} aria-label="Закрыть" disabled={busy}>×</button>
        </div>

        {!preview && (
          <div
            className={`ts-drop ${busy ? 'is-busy' : ''}`}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files[0]); }}
          >
            <p>
              Файл того же вида, что ведёт бухгалтерия: лист на месяц, в шапке «Сотрудник»,
              дни 1–31, справа ставка, аванс 1, аванс 2, итого, доплаты и штрафы.
            </p>
            <button type="button" className="ui-btn-primary" disabled={busy} onClick={() => inputRef.current?.click()}>
              {busy ? 'Читаю файл…' : 'Выбрать файл .xlsx'}
            </button>
            <input ref={inputRef} type="file" accept=".xlsx,.xlsm" hidden onChange={(e) => pick(e.target.files[0])} />
            <span className="ui-dim">или перетащите его сюда</span>
          </div>
        )}

        {error && <div className="ui-inline-error">{error}</div>}

        {preview && (
          <div className="ts-import-body">
            <p className="ts-import-summary">
              {file?.name}: {months.length} мес., с {months[0]?.label.toLowerCase()} по {last?.label.toLowerCase()}.
              {' '}{last?.label} останется открытым, остальные месяцы закроются — их расчёт уже выдан.
              Месяцы из файла заменят то, что сейчас в портале за те же месяцы.
            </p>

            <div className="ts-import-table-wrap">
              <table className="ts-import-table">
                <thead>
                  <tr>
                    <th scope="col">Месяц</th>
                    <th scope="col">Лист</th>
                    <th scope="col" className="ts-num-h">Людей</th>
                    <th scope="col" className="ts-num-h">Начислено</th>
                    <th scope="col" className="ts-num-h">«Итог» в файле</th>
                    <th scope="col" className="ts-num-h">Доплаты</th>
                  </tr>
                </thead>
                <tbody>
                  {[...months].reverse().map(m => {
                    const diff = m.file_total === null ? null : m.halves - m.file_total;
                    const off = diff !== null && Math.abs(diff) > 1;
                    return (
                      <tr key={`${m.year}-${m.month}`} className={off ? 'is-off' : ''}>
                        <th scope="row">{m.label}</th>
                        <td className="ui-dim">{m.sheet}</td>
                        <td className="ts-num">{m.people}</td>
                        <td className="ts-num">{money(m.halves)}</td>
                        <td className="ts-num">
                          {m.file_total === null ? <span className="ui-dim">нет строки</span> : money(m.file_total)}
                          {off && <span className="ts-diff">{diff > 0 ? '+' : '−'}{money(Math.abs(diff))}</span>}
                        </td>
                        <td className="ts-num">{m.bonus ? money(m.bonus) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {(mismatched.length > 0 || fileErrors.length > 0 || preview.warnings.length > 0 || preview.unknown_marks.length > 0) && (
              <div className="ts-import-notes">
                <h3>Что стоит знать</h3>
                <ul>
                  {mismatched.length > 0 && (
                    <li>
                      В {mismatched.length} мес. сумма строк не сходится с «Итог» файла
                      ({mismatched.map(m => m.label.toLowerCase()).join(', ')}). Строки сверены по одной —
                      расходится сама формула «Итог» на листе. Портал считает по строкам.
                    </li>
                  )}
                  {fileErrors.length > 0 && (
                    <li>
                      В {fileErrors.length} строках колонка «итого» не равна «аванс 1 + аванс 2» — чаще всего
                      у массажистов, где формула берёт только второй аванс. Например: {fileErrors.slice(-3).map(e => (
                        `${e.name} (${e.month.toLowerCase()}) — в файле ${money(e.file_total)}, по авансам ${money(e.halves)}`
                      )).join('; ')}. Портал возьмёт сумму авансов.
                    </li>
                  )}
                  {preview.warnings.map(w => <li key={w}>{w}</li>)}
                  {preview.unknown_marks.length > 0 && (
                    <li>
                      Незнакомые отметки сохранятся как есть и на расчёт не повлияют:{' '}
                      {preview.unknown_marks.map(m => `«${m.mark}» ×${m.count}`).join(', ')}.
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="ui-modal-actions">
          <button type="button" className="ui-btn-ghost" onClick={onClose} disabled={busy}>Отмена</button>
          {preview && (
            isAdmin ? (
              <button type="button" className="ui-btn-primary" onClick={apply} disabled={busy}>
                {busy ? 'Загружаю…' : `Загрузить ${months.length} мес.`}
              </button>
            ) : (
              <span className="ui-dim">Записать табель в базу может только владелец.</span>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default TimesheetImportModal;
